import {addPlaceholderBlots} from 'shells/email-editor/placeholder-utils';

import type {
    GetEmailSequenceTemplate_emailSequenceTemplate,
    EmailSequenceTemplateTemplate,
} from 'nutshell-graphql-types';

import {createSequenceTemplate, PLACEHOLDER_ID} from '../create-empty-sequence-template';

import {copyEmailSequenceTemplateForCurrentUser} from './copy-email-sequence-template-for-current-user';
import {getFirstEmailTemplateId} from './get-first-email-template-id';
import {normalizeEmailSequences} from './normalize-email-sequence-siblings';

function convertEmailSequenceTemplate(emailSequenceTemplate: EmailSequenceTemplateTemplate) {
    if (!emailSequenceTemplate) throw new Error('Attempted to show null EmailSequenceTemplate');

    const {emailSequenceTemplateMessages, ...restEmailSequenceTemplate} = emailSequenceTemplate;

    return {
        ...restEmailSequenceTemplate,
        emailSequenceTemplateMessages: emailSequenceTemplateMessages.reduce<Record<string, any>>(
            (acc, sequenceTemplateMessage) => {
                acc[sequenceTemplateMessage.id] = {
                    id: sequenceTemplateMessage.id,
                    position: sequenceTemplateMessage.position,
                    delay: {
                        timespan: sequenceTemplateMessage.sendDelay
                            ? sequenceTemplateMessage.sendDelay.delayTimespan
                            : 0,
                        unit: 'bzd',
                    },
                    subject: sequenceTemplateMessage.emailTemplate.subject,
                    bodyText: sequenceTemplateMessage.emailTemplate.plaintextBody,
                    bodyHTML: addPlaceholderBlots(sequenceTemplateMessage.emailTemplate.body),
                    sendAsReply: sequenceTemplateMessage.sendAsReply,
                    useSignature: sequenceTemplateMessage.useSignature,
                    sentCount: sequenceTemplateMessage.emailTemplate.sentCount,
                    clickCount: sequenceTemplateMessage.emailTemplate.clickCount,
                    openedCount: sequenceTemplateMessage.emailTemplate.openedCount,
                    error: null,
                    showError: false,
                };

                return acc;
            },
            {}
        ),
    };
}

/**
 * Get some state values for the EmailTemplateDesignerFormComponent.
 *
 * Usually, we would keep a state function like this in the originating file,
 * but this is better for adding tests around this function.
 *
 * This function:
 * * Gets the name of the email sequence template
 * * Gets the default selected sequence and template within that sequence
 * * Gets the transformed email sequence templates and their email templates
 * * Gets the sorted order of the email sequence templates
 *
 * This function is passed a "root" email sequence template. This is the
 * template the user click in the UI to open this modal. We get the name from
 * this sequence, as all sibling sequence templates have the same name. This is
 * also the template that we show to the user, so thats the default sequence
 * template. The first email template on the sequence is our default template.
 *
 * The normalized email sequences are an array of all related email sequence
 * templates. The root email sequence either has a parent with all of its
 * children, or is a parent itself and has an array with all of its children.
 * Those normalized sequences are then objectified (get it!) with their id as
 * the key.
 *
 * The sorted order of the email sequence templates also uses the normalized
 * sequences, but leaves them in an array, rather than an object, and sorts them
 * in the order that is expected for the sequence template list. It also adds an
 * empty sequence for the current user if they have no version of this sequence
 * template.
 *
 * @param sequenceTemplate The root sequence template
 */
export function getStateFromSequenceTemplate(
    sequenceTemplate: GetEmailSequenceTemplate_emailSequenceTemplate
) {
    let normalizedEmailSequences = normalizeEmailSequences(sequenceTemplate);

    if (sequenceTemplate.id !== PLACEHOLDER_ID) {
        if (!sequenceTemplate.userChildSequenceTemplate) {
            // If the current user doesn't have a copy, add an empty copy for the user
            normalizedEmailSequences = normalizedEmailSequences.concat(
                copyEmailSequenceTemplateForCurrentUser(sequenceTemplate)
            );
        } else if (sequenceTemplate.userChildSequenceTemplate.isArchived) {
            // If the childs sequence is archived, append it
            normalizedEmailSequences = normalizedEmailSequences.concat(
                sequenceTemplate.userChildSequenceTemplate
            );
        }
    }

    const normalizedEmailSequencesById = normalizedEmailSequences.reduce<Record<string, any>>(
        (acc, emailSequenceTemplate) => {
            acc[emailSequenceTemplate.id] = convertEmailSequenceTemplate(emailSequenceTemplate);

            return acc;
        },
        {}
    );
    const selectedSequenceId = sequenceTemplate.id;
    const emailSequenceTemplate = normalizedEmailSequencesById[selectedSequenceId];
    const selectedTemplateId = getFirstEmailTemplateId(emailSequenceTemplate);

    // -- SORTING --
    /*
    The email sequences need to be sorted to show to the user. The order is:

        * Current user's sequence template, or NULL if the current user doesn't
        have a template
        * Original template IF the current user's template isn't the original
        * All others, sorted by creator user's name
    */
    const userChildSequenceTemplateId = sequenceTemplate.userChildSequenceTemplate
        ? sequenceTemplate.userChildSequenceTemplate.id
        : PLACEHOLDER_ID;

    const userEmailSequenceTemplate = normalizedEmailSequences.find((emailSequence) => {
        return emailSequence.id === userChildSequenceTemplateId;
    });
    let sortedEmailSequenceTemplates = [
        userEmailSequenceTemplate ? userEmailSequenceTemplate : createSequenceTemplate(),
    ];
    let remainingEmailSequences = normalizedEmailSequences.filter((emailSequence) => {
        // Filter out the user's email sequence template
        return emailSequence.id !== userChildSequenceTemplateId;
    });

    // Second sequence should be the parent, unless the current user's sequence
    // is also the parent
    const parentSequence = remainingEmailSequences.find((emailSequence) => {
        return !emailSequence.parent;
    });
    if (parentSequence) {
        sortedEmailSequenceTemplates = sortedEmailSequenceTemplates.concat(parentSequence);
        remainingEmailSequences = remainingEmailSequences.filter((emailSequence) => {
            // Remove the parent sequence from the list of remaining sequences
            return emailSequence.id !== parentSequence.id;
        });
    }
    // Remaining should be sorted alphabetically by the user's name
    remainingEmailSequences = remainingEmailSequences.sort(
        (
            emailSequenceA: EmailSequenceTemplateTemplate,
            emailSequenceB: EmailSequenceTemplateTemplate
        ) => {
            const nameA = emailSequenceA.creatorUser.name.toUpperCase();
            const nameB = emailSequenceB.creatorUser.name.toUpperCase();

            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;

            return 0;
        }
    );

    sortedEmailSequenceTemplates = sortedEmailSequenceTemplates.concat(remainingEmailSequences);

    const sortedEmailSequenceTemplatesList = sortedEmailSequenceTemplates.map(
        (sortedEmailSequenceTemplate) => ({
            id: sortedEmailSequenceTemplate.id,
            name:
                // If this is the parent
                parentSequence && sortedEmailSequenceTemplate.id === parentSequence.id
                    ? `${sortedEmailSequenceTemplate.creatorUser.name} (original)`
                    : sortedEmailSequenceTemplate.creatorUser.name,
        })
    );

    return {
        name: emailSequenceTemplate.name,
        emailSequenceTemplates: normalizedEmailSequencesById,
        selectedTemplateId,
        selectedSequenceId,
        sortedEmailSequenceTemplates: sortedEmailSequenceTemplatesList,
    };
}
